import { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { FormDialog } from "@/components/common/FormDialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchInput } from "@/components/common/SearchInput";
import { PRIORITY_DOT, TYPE_COLOR } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Layers, Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const ITEM_TYPES = ["Epic", "Module", "Task", "Subtask", "Bug", "Feature", "Activity"];
const PRIORITIES = ["low", "medium", "high", "critical"];

export default function Backlog() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useApp();
  const [sprintFilter, setSprintFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");

  const { data: allItems = [], isLoading } = useQuery({ queryKey: ["items", id], queryFn: () => get(`/pm/projects/${id}/items`) });
  const items = useMemo(() => allItems.filter((i) => !i.in_planning), [allItems]);
  const { data: columns = [] } = useQuery({ queryKey: ["columns", id], queryFn: () => get(`/pm/projects/${id}/columns`) });
  const { data: sprints = [] } = useQuery({ queryKey: ["sprints", id], queryFn: () => get(`/pm/projects/${id}/sprints`) });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const umap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const cmap = useMemo(() => Object.fromEntries(columns.map((c) => [c.id, c.name])), [columns]);
  const smap = useMemo(() => Object.fromEntries(sprints.map((s) => [s.id, s.name])), [sprints]);

  const assignSprint = useMutation({
    mutationFn: ({ wid, sprint_id }) => patch(`/pm/items/${wid}`, { sprint_id }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["items", id] }); toast.success("Sprint updated"); },
  });
  const createSprint = useMutation({
    mutationFn: (body) => post(`/pm/projects/${id}/sprints`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["sprints", id] }); toast.success("Sprint created"); },
  });

  const bySprint = sprintFilter === "all" ? items
    : sprintFilter === "unassigned" ? items.filter((i) => !i.sprint_id)
    : items.filter((i) => i.sprint_id === sprintFilter);

  const filtered = useMemo(() => {
    return bySprint.filter((i) => {
      if (search && !i.title?.toLowerCase().includes(search.toLowerCase())) return false;
      if (typeFilter !== "all" && i.type !== typeFilter) return false;
      if (priorityFilter !== "all" && i.priority !== priorityFilter) return false;
      if (assigneeFilter !== "all" && i.assignee_id !== assigneeFilter) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bySprint, search, typeFilter, priorityFilter, assigneeFilter]);

  const isRealSprint = sprintFilter !== "all" && sprintFilter !== "unassigned";
  const { data: capacity } = useQuery({ queryKey: ["sprint-capacity", sprintFilter], queryFn: () => get(`/pm/sprints/${sprintFilter}/capacity`), enabled: isRealSprint });

  if (isLoading) return <Skeleton className="h-64" />;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <Select value={sprintFilter} onValueChange={setSprintFilter}>
          <SelectTrigger className="w-56" data-testid="sprint-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All items</SelectItem>
            <SelectItem value="unassigned">Unassigned to sprint</SelectItem>
            {sprints.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.status})</SelectItem>)}
          </SelectContent>
        </Select>
        {can("pm", "create") && (
          <FormDialog title="New Sprint" testid="new-sprint-modal"
            trigger={<Button size="sm" variant="outline" data-testid="new-sprint-btn"><Plus className="mr-1 h-3.5 w-3.5" />New Sprint</Button>}
            fields={[
              { name: "name", label: "Sprint Name", required: true, full: true, placeholder: "e.g. Sprint 5" },
              { name: "goal", label: "Sprint Goal", full: true },
              { name: "start", label: "Start", type: "date" },
              { name: "end", label: "End", type: "date" },
            ]}
            onSubmit={(v) => createSprint.mutateAsync({ ...v, start: v.start ? new Date(v.start).toISOString() : null, end: v.end ? new Date(v.end).toISOString() : null })} />
        )}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <SearchInput testid="backlog-search" placeholder="Search items" value={search} onChange={setSearch} />
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40" data-testid="backlog-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {ITEM_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-40" data-testid="backlog-priority-filter"><SelectValue placeholder="All priorities" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
          <SelectTrigger className="w-44" data-testid="backlog-assignee-filter"><SelectValue placeholder="All assignees" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All assignees</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isRealSprint && capacity && capacity.velocity_avg != null && (
        <div data-testid="sprint-capacity-strip" className={cn("mb-3 flex items-center gap-2 rounded-lg border px-3 py-2 text-xs",
          capacity.over_committed ? "border-red-300 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400"
            : "border-border bg-muted/40 text-muted-foreground")}>
          {capacity.over_committed && <AlertTriangle className="h-3.5 w-3.5" />}
          Committed {capacity.committed} pts vs. recent average velocity {capacity.velocity_avg} pts
          {capacity.over_committed && " — this sprint looks over-committed"}
        </div>
      )}

      {filtered.length === 0 ? <EmptyState icon={Layers} title="No items" subtitle="Nothing matches this filter." /> : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm" data-testid="backlog-table">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold">Title</th>
                <th className="px-4 py-3 font-semibold">Priority</th>
                <th className="px-4 py-3 font-semibold">Points</th>
                <th className="px-4 py-3 font-semibold">Assignee</th>
                <th className="px-4 py-3 font-semibold">Column</th>
                <th className="px-4 py-3 font-semibold">Sprint</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} data-testid={`backlog-row-${it.id}`}
                  className="border-b border-border last:border-0 hover:bg-muted/50">
                  <td className="cursor-pointer px-4 py-3" onClick={() => navigate(`/pm/projects/${id}/items/${it.id}`)}><StatusBadge family={TYPE_COLOR[it.type]} label={it.type} /></td>
                  <td className="cursor-pointer px-4 py-3 font-medium text-foreground" onClick={() => navigate(`/pm/projects/${id}/items/${it.id}`)}>{it.title}</td>
                  <td className="px-4 py-3"><span className="flex items-center gap-1.5 capitalize"><span className={cn("h-2 w-2 rounded-full", PRIORITY_DOT[it.priority])} />{it.priority}</span></td>
                  <td className="px-4 py-3 font-mono">{it.story_points}</td>
                  <td className="px-4 py-3"><div className="flex items-center gap-1.5"><Avatar user={umap[it.assignee_id]} size="xs" /><span className="text-xs">{umap[it.assignee_id]?.name}</span></div></td>
                  <td className="px-4 py-3"><StatusBadge family="slate" label={cmap[it.column_id]} /></td>
                  <td className="px-4 py-3">
                    <Select value={it.sprint_id || "none"} onValueChange={(v) => assignSprint.mutate({ wid: it.id, sprint_id: v === "none" ? null : v })}>
                      <SelectTrigger className="h-7 w-36" data-testid={`sprint-select-${it.id}`}><SelectValue placeholder="Backlog" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Backlog</SelectItem>
                        {sprints.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
